import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LegacyGenreResponse } from '../types';
import { gameService } from '../services/api';
import GameList from '../components/GameList';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';
import { GameListSkeleton } from '../components/skeletons';
import { Search, ArrowLeft } from 'lucide-react';

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<LegacyGenreResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = searchParams.get('q') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    if (query) {
      fetchSearchResults(query, currentPage);
    }
  }, [query, currentPage]);

  const fetchSearchResults = async (searchQuery: string, page: number) => {
    try {
      setLoading(true);
      setError(null);
      const results = await gameService.searchGames({ q: searchQuery, page });
      setSearchResults(results);
    } catch (err) {
      setError('Erreur lors de la recherche');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };


  const handlePageChange = (page: number) => {
    setSearchParams({ q: query, page: page.toString() });
    // Scroll smooth vers le haut
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Recherche
            </h1>
          </div>
          
          <SearchBar
            placeholder="Rechercher un jeu..."
            className="max-w-2xl"
          />
        </div>
      </div>

      {/* Search Results */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {query && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Résultats pour "{query}"
            </h2>
            {searchResults && (
              <p className="text-gray-600 dark:text-gray-400">
                {searchResults.total_games} jeu{searchResults.total_games > 1 ? 'x' : ''} trouvé{searchResults.total_games > 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        {loading ? (
          <GameListSkeleton count={12} />
        ) : searchResults ? (
          <>
            <GameList games={searchResults.games} error={error} />
            
            {searchResults.total_pages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={searchResults.current_page}
                  totalPages={searchResults.total_pages}
                  hasNext={searchResults.has_next}
                  hasPrevious={searchResults.has_previous}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : !query ? (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Recherchez un jeu
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Utilisez la barre de recherche ci-dessus pour trouver des jeux
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Aucun résultat trouvé
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Essayez avec d'autres mots-clés
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
